import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import {
  addEntry,
  type DayEntry,
  type DayGap,
  type DayReportPageData,
  DayReportGone,
  type DaySchedule,
  removeEntry,
  saveAway,
  saveMail,
  saveWorkingHours,
  updateEntry,
  dayReportQuery,
} from '../api/dayReport'
import { SettingsRadio } from '../ui/SettingsParts'
import { Spinner } from '../ui/Spinner'
import './DayReportPage.css'

/**
 * =============================================================================
 * Raportul de seara al unui om - PAGINA PUBLICA, deschisa din linkul emailului
 * =============================================================================
 *
 * Omul din fata ei n-are cont, n-are sesiune si, de cele mai multe ori, n-are
 * decat telefonul: linkul vine intr-un email de seara. De-aia pagina:
 *
 *  - se deseneaza pe langa scheletul aplicatiei (vezi `barePages.ts`) - fara
 *    antet, fara bara de module, fara alegator de firma;
 *  - nu duce NICAIERI in aplicatie. Niciun `<Link>`, niciun `/app/...`: o
 *    legatura catre portal, apasata de cineva fara cont, duce la "Sign in", si
 *    omul n-ar afla niciodata de ce;
 *  - nu intreaba nimic autentificat. Totul trece prin `/api/v2/public/...`.
 *
 * -----------------------------------------------------------------------------
 * CE FACE PAGINA, IN ORDINEA IN CARE E SCRISA
 * -----------------------------------------------------------------------------
 *
 * Intai ziua asa cum a fost masurata, apoi GOLURILE, si abia dupa ele setarile.
 * Golurile stau sus fiindca ele sunt motivul pentru care a fost trimis linkul;
 * daca ar sta sub program si sub concediu, cel care deschide emailul pe telefon
 * n-ar ajunge la ele.
 *
 * -----------------------------------------------------------------------------
 * MASURAT SI SCRIS DE OM NU SE AMESTECA
 * -----------------------------------------------------------------------------
 *
 * Ce e masurat sta in cifrele de sus; ce a scris omul sta sub goluri, sub
 * „Added by you", si fiecare rand o spune in cuvant (insigna "added by you") si
 * in desen (dunga din stanga). Serverul refuza un rand care ar acoperi ceas
 * masurat, si pagina o spune cu propozitia serverului - nu cu una scrisa aici,
 * ca sa nu ajunga cele doua sa spuna lucruri diferite despre aceeasi regula.
 *
 * Pana pe 5 octombrie 2026 exista si o sectiune „Your summary of the day", cu o
 * linie "measured" care repeta cifrele de sus. A fost scoasa, aici si in
 * scrisoare (`DayReportEmail`, in v4/api si in ph4.api) - vezi randurile scrise
 * de om, mai jos.
 */

/* ------------------------------------------------------------------ marunte */

/** `9000` -> `2 h 30 m`. Ore si minute, niciodata secunde: nimeni nu citeste secunde. */
function duration(seconds: number): string {
  if (seconds <= 0) return '0 m'
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.round((seconds - hours * 3600) / 60)
  if (hours === 0) return `${minutes} m`
  if (minutes === 0) return `${hours} h`
  return `${hours} h ${minutes} m`
}

/** `2026-09-21` -> `21 September 2026`, fara sa depinda de limba browserului. */
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

function longDate(day: string): string {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day)
  if (parts === null) return day
  return `${Number(parts[3])} ${MONTHS[Number(parts[2]) - 1] ?? ''} ${parts[1]}`
}

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : 'That could not be saved.'
}

/**
 * De unde vine ora la care pleaca mailul, spusa omului.
 *
 * Numele treptelor sunt ale lui `DaySchedule`, din jumatatea care trimite
 * emailul; aici se traduc o singura data, ca sa nu apara pe ecran cuvinte de
 * cod. O treapta necunoscuta - una adaugata acolo mai tarziu - nu strica
 * nimic: se scrie doar ora, fara explicatie.
 */
function scheduleSource(source: string): string {
  if (source === 'user_hour') return 'the time you picked'
  if (source === 'user_hours') return 'the end of your working day'
  if (source === 'device_hours') return 'the end of the working day on your machine'
  if (source === 'default_day') return "your company's default working day"
  if (source === 'fallback') return 'the standard time, until you pick one'
  return ''
}

/* ------------------------------------------------------------- marca */

/**
 * Antetul de marca: sigla OptimumDesk si modulul, la fel ca in capul emailului
 * de seara ("OptimumDesk · Productivitee"), ca omul care deschide linkul sa
 * stie de unde vine pagina. Sigla e cea pe care o are deja portalul
 * (`images/logo/OptimumDeskHeader.png`) - marcaj colorat plus cuvantul alb, deci
 * se vede pe fundalul de culoare al modulului. Nu se aduce o imagine noua.
 */
function BrandBar() {
  return (
    <div className="odr-brandbar">
      <img className="odr-logo" src={`${import.meta.env.BASE_URL}images/logo/OptimumDeskHeader.png`} alt="OptimumDesk" />
      <span className="odr-eyebrow">Productivitee</span>
    </div>
  )
}

/* ------------------------------------------------------------- linkul mort */

/**
 * Ce vede cineva cu un link vechi, retras sau scris gresit.
 *
 * UN SINGUR TEXT PENTRU TOATE. Nu se spune care din ele, si nu se spune nimic
 * despre ce ar fi fost dincolo - nici un nume, nici o zi, nici macar ca zilei
 * aceleia i s-ar fi trimis vreodata un email. Deosebirea nu-i foloseste cu
 * nimic omului care a apasat linkul, si ii spune unui strain care incearca
 * siruri la rand ca a nimerit unul adevarat.
 *
 * Nu are niciun buton: n-avem unde sa-l trimitem. Linkul nou vine cu urmatorul
 * email, si asta e chiar ce scrie pe ecran.
 */
function LinkGone() {
  return (
    <div className="odr">
      <div className="odr-head">
        <div className="odr-wrap">
          <BrandBar />
          <h1>This link is no longer valid</h1>
        </div>
      </div>
      <div className="odr-wrap">
        <div className="odr-card">
          <p style={{ margin: '0 0 10px' }}>
            Links in the daily activity email stop working after a while, so that an old message cannot open your day
            later on.
          </p>
          <p style={{ margin: 0 }}>Your next daily email will carry a fresh link.</p>
        </div>
      </div>
    </div>
  )
}

/* ----------------------------------------------------------------- formular */

interface EntryDraft {
  start: string
  end: string
  kind: number
  note: string
}

/**
 * Formularul unui rand - acelasi si pentru unul nou, si pentru unul schimbat.
 *
 * `onSave` primeste ce s-a scris ca ARGUMENT. Mutatia de deasupra il ia de
 * acolo si il duce mai departe tot ca argument (`mutate(data)`), niciodata
 * dintr-un `useState` inchis intr-o functie: un `mutate()` care citeste starea
 * prin inchidere trimite valoarea de la randarea in care a fost creata functia,
 * si asta se vede abia cand un om apasa repede de doua ori.
 */
function EntryForm({
  draft,
  kinds,
  maxNote,
  saving,
  error,
  onSave,
  onCancel,
}: {
  draft: EntryDraft
  kinds: { id: number; label: string }[]
  maxNote: number
  saving: boolean
  error: string
  onSave(fields: EntryDraft): void
  onCancel(): void
}) {
  const [fields, setFields] = useState<EntryDraft>(draft)

  /* Golul pe care se deschide formularul se poate schimba sub el (alt gol
     apasat cat era deschis), si atunci orele trebuie sa vina cu el. */
  useEffect(() => {
    setFields(draft)
  }, [draft])

  return (
    <form
      className="odr-form"
      onSubmit={(event) => {
        event.preventDefault()
        onSave(fields)
      }}
    >
      <div className="odr-field odr-times">
        <div>
          <label htmlFor="odr-start">From</label>
          <input
            id="odr-start"
            className="odr-input"
            type="time"
            value={fields.start}
            onChange={(event) => setFields({ ...fields, start: event.target.value })}
          />
        </div>
        <div>
          <label htmlFor="odr-end">To</label>
          <input
            id="odr-end"
            className="odr-input"
            type="time"
            value={fields.end}
            onChange={(event) => setFields({ ...fields, end: event.target.value })}
          />
        </div>
      </div>

      <div className="odr-field">
        <span className="odr-legend">What were you doing?</span>
        <div className="odr-kinds">
          {kinds.map((kind) => (
            <SettingsRadio
              key={kind.id}
              name="odr-kind"
              value={kind.id}
              label={kind.label}
              checked={fields.kind === kind.id}
              onChange={(on) => {
                if (on) setFields({ ...fields, kind: kind.id })
              }}
            />
          ))}
        </div>
      </div>

      <div className="odr-field">
        <label htmlFor="odr-note">In your own words (optional)</label>
        <textarea
          id="odr-note"
          className="odr-textarea"
          maxLength={maxNote}
          value={fields.note}
          placeholder="Project meeting with the team"
          onChange={(event) => setFields({ ...fields, note: event.target.value })}
        />
      </div>

      {error !== '' && <p className="odr-note odr-note-bad">{error}</p>}

      <div className="odr-actions">
        <button type="submit" className="odr-btn" disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="odr-btn odr-btn-quiet" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
      </div>
    </form>
  )
}

/* ================================================================== PAGINA */

export function DayReportPage({ token, gap }: { token: string; gap?: string }) {
  const client = useQueryClient()
  const page = useQuery(dayReportQuery(token))

  /* Ce formular e deschis: un gol (`gap:<index>`), un rand al lui
     (`entry:<id>`), sau nimic. Unul singur deodata - pe un telefon, doua
     formulare deschise inseamna ca al doilea nu se vede. */
  const [open, setOpen] = useState<string>('')

  const refresh = () => {
    void client.invalidateQueries({ queryKey: ['productivitee', 'day-report', token] })
  }

  const write = useMutation({
    mutationFn: (job: { kind: 'add'; fields: EntryDraft } | { kind: 'edit'; id: number; fields: EntryDraft } | { kind: 'remove'; id: number }) => {
      if (job.kind === 'add') return addEntry(token, job.fields)
      if (job.kind === 'edit') return updateEntry(token, job.id, job.fields)
      return removeEntry(token, job.id)
    },
    onSuccess: () => {
      setOpen('')
      refresh()
    },
  })

  /*
   * ============================================================================
   * BUCATA PE CARE A APASAT IN EMAIL SE DESCHIDE SINGURA
   * ============================================================================
   *
   * Cerinta Roxanei, 25 septembrie 2026: fiecare bucata de timp neurmarit din
   * scrisoare e un link, si linkul trebuie sa deschida pagina CHIAR la bucata
   * aia. Aici se face partea paginii: se cauta ceasul din adresa
   * (`?gap=HH:MM`) printre bucatile zilei, se deschide formularul ei si se
   * aduce sub ochi.
   *
   * O SINGURA DATA, si de-aia exista `jumped`. Fara el, orice reincarcare a
   * datelor - salvarea unui rand, revenirea in fereastra - ar inchide
   * formularul pe care omul tocmai il deschisese in alta parte si l-ar arunca
   * inapoi la bucata din adresa. Adresa spune de unde se PORNESTE, nu unde
   * trebuie sa stea.
   *
   * CAND NU SE POTRIVESTE NIMIC NU SE INTAMPLA NIMIC, si asta e purtarea
   * corecta, nu o scapare: bucata poate sa fi fost deja lamurita (serverul o
   * scoate din lista, `Gaps::open()`), sau ziua se poate fi umplut intre timp.
   * Un mesaj de eroare pentru un link care a lucrat exact cum trebuia ar fi mai
   * rau decat tacerea.
   *
   * `scrollIntoView` se cheama cu `?.`: in jsdom, unde se rulează probele, nu
   * exista, si o proba n-are de ce sa pice pe o derulare.
   */
  const gaps = page.data?.gaps
  const [jumped, setJumped] = useState(false)

  useEffect(() => {
    if (jumped || gap === undefined || gaps === undefined) return

    /* Semnul se pune ORICUM, si inainte de a sti daca s-a gasit ceva: un ceas
       care nu se potriveste nu are voie sa fie cautat din nou la fiecare
       reincarcare. */
    setJumped(true)

    const index = gaps.findIndex((one) => one.start === gap)
    if (index < 0) return

    setOpen(`gap:${index}`)
    document.getElementById(`odr-gap-${index}`)?.scrollIntoView?.({ block: 'center' })
  }, [gap, gaps, jumped])

  if (page.error instanceof DayReportGone) return <LinkGone />
  if (page.isError) return <LinkGone />
  if (!page.data) return <Spinner />

  const data: DayReportPageData = page.data
  const writeError = write.isError ? messageOf(write.error) : ''

  const draftFor = (gap: DayGap): EntryDraft => ({ start: gap.start, end: gap.end, kind: data.kinds[0]?.id ?? 1, note: '' })
  const draftOf = (entry: DayEntry): EntryDraft => ({
    start: entry.start_time,
    end: entry.end_time,
    kind: entry.kind,
    note: entry.note,
  })

  return (
    <div className="odr" id="odr-top">
      <div className="odr-head">
        <div className="odr-wrap">
          <BrandBar />
          <h1>Your day — {data.weekday}, {longDate(data.day)}</h1>
          <p>{data.person.name}</p>
        </div>
      </div>

      <div className="odr-wrap">
        {data.away && (
          <p className="odr-note odr-note-quiet">
            You were out of office on this day, so nothing is expected of you here.
          </p>
        )}

        {/*
         * Pe telefon setarile stau la capatul raportului, si cine deschide
         * emailul poate sa nu ajunga niciodata la ele. Un link scurt sare direct
         * acolo. Pe desktop nu se vede (`@media`): acolo setarile stau in coloana
         * din dreapta, sub ochi de la primul cadru.
         */}
        <a className="odr-jump" href="#odr-settings">
          Settings
        </a>

        <div className="odr-columns">
          <div className="odr-main">
        {/* ------------------------------------------------- ziua masurata */}

        <dl className="odr-figures">
          <div className="odr-figure">
            <dt>Started</dt>
            <dd>{data.measured.start}</dd>
          </div>
          <div className="odr-figure">
            <dt>Finished</dt>
            <dd>{data.measured.end}</dd>
          </div>
          <div className="odr-figure">
            <dt>Active time</dt>
            <dd>
              {duration(data.measured.activeSeconds)}
              <small>
                {data.measured.activeShare}% of {duration(data.measured.coveredSeconds)} measured
              </small>
            </dd>
          </div>
          <div className="odr-figure">
            {/*
             * Productive time nu se masoara INCA pe om: sumarul zilnic al unei
             * persoane (`it_productivitee_user_summary`) tine lucrat / activ /
             * inactiv, dar timpul productiv sta doar pe sumarul APARATULUI. Un
             * numar scos de aici ar minti (azi tot ce nu e etichetat trece drept
             * productiv), asa ca se arata gol, cu o vorba, nu o cifra inventata.
             */}
            <dt>Productive time</dt>
            <dd>
              —<small>not measured per person yet</small>
            </dd>
          </div>
        </dl>

        {/* ------------------------------------------------------- golurile */}

        <section className="odr-card">
          <h2>Untracked time</h2>
          {/*
            * ACELASI CUVANT CA IN SCRISOARE: „untracked time".
            *
            * Cerinta Roxanei, 25 septembrie 2026: „Gaps" nu l-au inteles
            * colegii, si cuvantul nou trebuie sa fie acelasi in email si pe
            * pagina. Propozitia de sub titlu spune ce ESTE, cu aceleasi cuvinte
            * ca `DayReportEmail::gapsBlock()`.
            *
            * UN SINGUR NUME PENTRU ACELASI LUCRU. Pana in aceeasi zi titlul
            * suna „Hours without tracking or activity" si propozitia de sub el
            * incepea cu „Untracked time" - adica acelasi lucru cu doua nume
            * noi, pe acelasi ecran, unul sub altul. Tocmai scosesem „Gaps"
            * fiindca nu se intelegea; doua nume in locul lui nu repara nimic.
            * Acum titlul e chiar cuvantul din propozitie, si tot el e si in
            * scrisoare - si acolo era si numele casutei de cifre.
            *
            * ATENTIE la cifra: pragul paginii vine de la server
            * (`Gaps::MIN_SECONDS`, 10 minute) si NU e acelasi cu al scrisorii
            * (`DayReportSender::GAP_SECONDS`, 15 minute). De-aia se scrie de
            * aici, din `limits`, si nu ca o cifra pusa de mana: o pagina care ar
            * promite 15 minute si ar arata bucati de 12 ar fi mai rea decat una
            * care spune adevarul ei.
            */}
          <p className="odr-lede">
            Untracked time is any stretch of your day, longer than {data.limits.minGapMinutes} minutes, in which none of
            your machines recorded any activity. The agent sees what is on your screen, so a meeting, a phone call or a
            trip to a client looks the same as a break. Write what you were doing — the figures above stay exactly as
            they were measured.
          </p>

          {/*
           * Adaugarea larga: acelasi formular, dar cu orele goale, pentru cine
           * vrea sa noteze ceva fara sa porneasca de la un gol anume. Serverul
           * tot cere ca randul sa cada intr-un gol (raspunde 409 peste ceas
           * masurat), iar formularul arata acea propozitie a serverului -
           * regula nu se schimba aici, doar se scoate la vedere.
           */}
          {open === 'add' ? (
            <EntryForm
              draft={{ start: '', end: '', kind: data.kinds[0]?.id ?? 1, note: '' }}
              kinds={data.kinds}
              maxNote={data.limits.maxNote}
              saving={write.isPending}
              error={writeError}
              onCancel={() => setOpen('')}
              onSave={(fields) => write.mutate({ kind: 'add', fields })}
            />
          ) : (
            <button type="button" className="odr-btn odr-btn-quiet odr-add-extra" onClick={() => setOpen('add')}>
              Add extra activity for the day
            </button>
          )}

          {/*
           * VESTEA BUNA, SPUSA SIMPLU.
           *
           * Cerinta Roxanei, 25 septembrie 2026. Pana atunci scria „Nothing
           * unaccounted for. Your whole working day was covered." - limbaj de
           * birocrat pentru ceva care e, de fapt, o veste buna. Aceleasi
           * cuvinte si in scrisoare (`DayReportEmail::gapsBlock()` si varianta
           * text a ei), ca sa nu existe doua mesaje.
           */}
          {data.gaps.length === 0 ? (
            <p className="odr-empty">Your whole working day was covered. There is nothing to fill in.</p>
          ) : (
            data.gaps.map((gap, index) => {
              const id = `gap:${index}`
              return (
                <div className="odr-gap" id={`odr-gap-${index}`} key={`${gap.start}-${gap.end}`}>
                  <div className="odr-gap-head">
                    <span className="odr-gap-when">
                      {gap.start} – {gap.end}
                    </span>
                    <span className="odr-gap-long">{duration(gap.seconds)} unaccounted for</span>
                    {open !== id && (
                      <button type="button" className="odr-btn" onClick={() => setOpen(id)}>
                        Say what you were doing
                      </button>
                    )}
                  </div>
                  {open === id && (
                    <EntryForm
                      draft={draftFor(gap)}
                      kinds={data.kinds}
                      maxNote={data.limits.maxNote}
                      saving={write.isPending}
                      error={writeError}
                      onCancel={() => setOpen('')}
                      onSave={(fields) => write.mutate({ kind: 'add', fields })}
                    />
                  )}
                </div>
              )
            })
          )}

          {/*
           * RANDURILE SCRISE DE OM, AICI, SUB GOLURI.
           *
           * Cerinta Roxanei, 5 octombrie 2026: sectiunea „Your summary of the
           * day" a fost scoasa din pagina si din scrisoare - linia ei masurata
           * (inceput - sfarsit, procentul activ) repeta cifrele de sus. Ce avea
           * DOAR ea erau randurile adaugate de om, cu Change / Remove; fara ele
           * omul n-ar mai fi putut indrepta ce a scris. Au venit aici, in
           * cardul de unde se si adauga, si numai cand exista. Timpul de la
           * masina (`coveredSeconds`), al doilea lucru pe care il avea numai ea,
           * a urcat langa procentul din casuta „Active time".
           */}
          {data.entries.length > 0 && (
            <div className="odr-mine">
              <h3 className="odr-sub">Added by you</h3>
              {data.entries.map((entry) => {
                const id = `entry:${entry.id}`
                return (
                  <div className="odr-row odr-row-mine" key={entry.id}>
                    <span className="odr-row-when">
                      {entry.start_time} – {entry.end_time}
                    </span>
                    <span className="odr-badge odr-badge-mine">added by you</span>
                    <span className="odr-row-what">
                      {entry.kindLabel}
                      {entry.note !== '' && ` — ${entry.note}`}
                    </span>
                    <span className="odr-row-tools">
                      <button type="button" className="odr-btn-link" onClick={() => setOpen(open === id ? '' : id)}>
                        {open === id ? 'Close' : 'Change'}
                      </button>
                      <button
                        type="button"
                        className="odr-btn-link odr-danger"
                        disabled={write.isPending}
                        onClick={() => write.mutate({ kind: 'remove', id: entry.id })}
                      >
                        Remove
                      </button>
                    </span>
                    {open === id && (
                      <div style={{ flex: '1 1 100%' }}>
                        <EntryForm
                          draft={draftOf(entry)}
                          kinds={data.kinds}
                          maxNote={data.limits.maxNote}
                          saving={write.isPending}
                          error={writeError}
                          onCancel={() => setOpen('')}
                          onSave={(fields) => write.mutate({ kind: 'edit', id: entry.id, fields })}
                        />
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </section>

        {/* ------------------------------------------------------ programe */}

        {data.applications.length > 0 && (
          <section className="odr-card">
            <h2>Your activity for the day</h2>
            <p className="odr-lede">The applications that took the most time, measured by the agent.</p>
            <ul className="odr-apps">
              {data.applications.map((app) => {
                const widest = data.applications[0]?.seconds ?? 1
                return (
                  <li className="odr-app" key={app.name}>
                    <div className="odr-app-line">
                      <span className="odr-app-name">{app.name === '' ? 'no name' : app.name}</span>
                      <span className="odr-app-time">{duration(app.seconds)}</span>
                    </div>
                    <div
                      className="odr-app-bar"
                      style={{ width: `${Math.max(2, Math.round((app.seconds / Math.max(1, widest)) * 100))}%` }}
                    />
                  </li>
                )
              })}
            </ul>
          </section>
        )}

          </div>{/* .odr-main */}

          {/*
           * Setarile, adunate intr-un singur loc. Pe desktop stau in coloana din
           * dreapta (vezi `@media`), vizibile fara sa derulezi; pe telefon vin
           * dupa raport, dar linkul de sus sare direct la ele.
           */}
          <aside className="odr-side" id="odr-settings">
            <h2 className="odr-side-head">Settings</h2>
            <WorkingHours token={token} schedule={data.schedule} onSaved={refresh} />
            <EmailSchedule token={token} schedule={data.schedule} onSaved={refresh} />
            <OutOfOffice token={token} today={data.day} schedule={data.schedule} onSaved={refresh} />
            <a className="odr-back-top" href="#odr-top">
              Back to top
            </a>
          </aside>
        </div>{/* .odr-columns */}
      </div>
    </div>
  )
}

/* ============================================================ ORELE DE LUCRU */

/**
 * Programul omului: o ora de inceput si una de sfarsit.
 *
 * Tot el hotaraste cand se incheie ziua - deci si cand pleaca emailul, cat timp
 * omul nu si-a ales alta ora. Scrie si pe ecran, ca sa nu para doua setari fara
 * legatura.
 *
 * Nu sunt orele MASINII (Productivitee > Settings > Business hours): acolo
 * programul e al unui aparat, si un aparat pe care lucreaza doua schimburi n-are
 * programul niciunuia dintre cei doi oameni. Lasate amandoua goale, se cade
 * inapoi pe programul masinii si apoi pe cel implicit - vezi `DaySchedule`.
 */
function WorkingHours({ token, schedule, onSaved }: { token: string; schedule: DaySchedule; onSaved(): void }) {
  const [start, setStart] = useState(schedule.workStart ?? '')
  const [end, setEnd] = useState(schedule.workEnd ?? '')
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    setStart(schedule.workStart ?? '')
    setEnd(schedule.workEnd ?? '')
  }, [schedule.workStart, schedule.workEnd])

  const save = useMutation({
    mutationFn: (hours: { start: string; end: string }) => saveWorkingHours(token, hours),
    onSuccess: () => {
      setSaved(true)
      onSaved()
    },
  })

  return (
    <section className="odr-card">
      <h2>Your working hours</h2>
      <p className="odr-lede">
        These are yours, not the machine&apos;s. They decide which part of the day is expected to be accounted for —
        and, unless you pick another time below, when this email reaches you. Leave both empty to go back to the
        working day set for your machine.
      </p>

      <div className="odr-field odr-times">
        <div>
          <label htmlFor="odr-work-start">My day starts at</label>
          <input
            id="odr-work-start"
            className="odr-input"
            type="time"
            value={start}
            onChange={(event) => {
              setStart(event.target.value)
              setSaved(false)
            }}
          />
        </div>
        <div>
          <label htmlFor="odr-work-end">and ends at</label>
          <input
            id="odr-work-end"
            className="odr-input"
            type="time"
            value={end}
            onChange={(event) => {
              setEnd(event.target.value)
              setSaved(false)
            }}
          />
        </div>
      </div>

      {save.isError && <p className="odr-note odr-note-bad">{messageOf(save.error)}</p>}
      {saved && !save.isError && <p className="odr-note odr-note-ok">Your working hours were saved.</p>}

      <button type="button" className="odr-btn" disabled={save.isPending} onClick={() => save.mutate({ start, end })}>
        {save.isPending ? 'Saving…' : 'Save working hours'}
      </button>
    </section>
  )
}

/* ============================================================ ORA EMAILULUI */

function EmailSchedule({ token, schedule, onSaved }: { token: string; schedule: DaySchedule; onSaved(): void }) {
  type Choice = 'end' | 'time' | 'never'
  const current: Choice = !schedule.enabled ? 'never' : schedule.sendHour === null ? 'end' : 'time'
  const [choice, setChoice] = useState<Choice>(current)
  const [hour, setHour] = useState(schedule.sendHour ?? schedule.effectiveHour)
  const [saved, setSaved] = useState(false)

  const save = useMutation({
    mutationFn: (next: { enabled: boolean; hour: string | null }) => saveMail(token, next),
    onSuccess: () => {
      setSaved(true)
      onSaved()
    },
  })

  const pick = (next: Choice) => {
    setChoice(next)
    setSaved(false)
  }

  const source = scheduleSource(schedule.effectiveSource)

  return (
    <section className="odr-card">
      <h2>When this email reaches you</h2>
      {schedule.enabled && (
        <p className="odr-lede">
          {/* Ora ADEVARATA, nu o promisiune: "cand se termina programul meu"
              nu spune nimic daca omul nu stie ce ora e aia. */}
          Right now it goes out at <strong>{schedule.effectiveHour}</strong>
          {source === '' ? '' : `, ${source}`}.
        </p>
      )}

      <div className="odr-field">
        <SettingsRadio
          name="odr-mail"
          value="end"
          label="When my working day ends"
          checked={choice === 'end'}
          onChange={(on) => on && pick('end')}
        />
      </div>
      <div className="odr-field">
        <SettingsRadio
          name="odr-mail"
          value="time"
          label="At a time I pick"
          checked={choice === 'time'}
          onChange={(on) => on && pick('time')}
        />
        {choice === 'time' && (
          <input
            className="odr-input"
            type="time"
            aria-label="Send the email at"
            style={{ marginTop: 8, maxWidth: 160 }}
            value={hour}
            onChange={(event) => {
              setHour(event.target.value)
              setSaved(false)
            }}
          />
        )}
      </div>
      <div className="odr-field">
        <SettingsRadio
          name="odr-mail"
          value="never"
          label="Do not send it at all"
          checked={choice === 'never'}
          onChange={(on) => on && pick('never')}
        />
      </div>

      {save.isError && <p className="odr-note odr-note-bad">{messageOf(save.error)}</p>}
      {saved && !save.isError && <p className="odr-note odr-note-ok">Saved.</p>}

      <button
        type="button"
        className="odr-btn"
        disabled={save.isPending}
        onClick={() => save.mutate({ enabled: choice !== 'never', hour: choice === 'time' ? hour : null })}
      >
        {/*
         * "Save email time", nu "Save". Pagina are trei butoane de salvare -
         * randul, programul si asta - si trei butoane care scriu acelasi cuvant
         * nu se deosebesc nici cu degetul pe telefon, nici cu cititorul de
         * ecran, care le anunta pe toate trei la fel.
         */}
        {save.isPending ? 'Saving…' : 'Save email time'}
      </button>
    </section>
  )
}

/* ================================================================= CONCEDIU */

/**
 * O SINGURA perioada, scrisa peste cea de dinainte.
 *
 * Nu e o lista de concedii - randul de setari al omului e unul singur
 * (`DayReportSettings`). Golirea amandurora o sterge, si asta scrie pe buton,
 * ca sa nu fie nevoie de un al doilea.
 */
function OutOfOffice({
  token,
  today,
  schedule,
  onSaved,
}: {
  token: string
  today: string
  schedule: DaySchedule
  onSaved(): void
}) {
  const [from, setFrom] = useState(schedule.awayFrom ?? '')
  const [to, setTo] = useState(schedule.awayTo ?? '')
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    setFrom(schedule.awayFrom ?? '')
    setTo(schedule.awayTo ?? '')
  }, [schedule.awayFrom, schedule.awayTo])

  const save = useMutation({
    mutationFn: (period: { from: string; to: string }) => saveAway(token, period),
    onSuccess: () => {
      setSaved(true)
      onSaved()
    },
  })

  const set = schedule.awayFrom !== null

  return (
    <section className="odr-card">
      <h2>Out of office</h2>
      <p className="odr-lede">
        No daily email goes out on these days, and they are kept out of what is expected of you. Leave the last day
        empty for a single day off.
      </p>

      {set ? (
        <div className="odr-row">
          <span className="odr-row-when">
            {schedule.awayFrom === schedule.awayTo ? schedule.awayFrom : `${schedule.awayFrom} – ${schedule.awayTo}`}
          </span>
          <span className="odr-row-what">You are marked as away.</span>
        </div>
      ) : (
        <p className="odr-empty">Nothing set.</p>
      )}

      <form
        className="odr-form"
        onSubmit={(event) => {
          event.preventDefault()
          save.mutate({ from, to })
        }}
      >
        <div className="odr-field odr-times">
          <div>
            <label htmlFor="odr-away-from">First day</label>
            <input
              id="odr-away-from"
              className="odr-input"
              type="date"
              min={today}
              value={from}
              onChange={(event) => {
                setFrom(event.target.value)
                setSaved(false)
              }}
            />
          </div>
          <div>
            <label htmlFor="odr-away-to">Last day (optional)</label>
            <input
              id="odr-away-to"
              className="odr-input"
              type="date"
              value={to}
              onChange={(event) => {
                setTo(event.target.value)
                setSaved(false)
              }}
            />
          </div>
        </div>

        {save.isError && <p className="odr-note odr-note-bad">{messageOf(save.error)}</p>}
        {saved && !save.isError && <p className="odr-note odr-note-ok">Saved.</p>}

        <button type="submit" className="odr-btn" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : from === '' && to === '' ? 'Clear my days off' : 'Save my days off'}
        </button>
      </form>
    </section>
  )
}
